import React, { useState, useEffect, useRef } from 'react';
import { UserRound, KeyRound, Shield, Zap, Smartphone, Sparkles, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import PasskeyRegisterPanel from '@/components/PasskeyRegisterPanel';
import { clearPasskeyPromptFromStoredUser } from '../_helpers/passkeyOnboarding';
import { passkeyRemindLater, PASSKEY_REMIND_AFTER_DAYS, isPasskeyRemindLaterSuccess } from '../_services/passkey.service';

/**
 * Passkey onboarding: promo → register. Closing from promo calls remind-later; closing from register (X / Escape) returns to promo like Back.
 */
export default function PasskeyOnboardingModal({
    open,
    onOpenChange,
    userId,
    username,
    onRegistered,
}) {
    const [step, setStep] = useState('promo');
    const [remindLoading, setRemindLoading] = useState(false);
    const [remindError, setRemindError] = useState('');
    const closedAfterSuccessRef = useRef(false);
    const stepRef = useRef('promo');
    const isSupported = typeof window !== 'undefined' && !!window.PublicKeyCredential;

    useEffect(() => {
        stepRef.current = step;
    }, [step]);

    useEffect(() => {
        if (open) {
            setStep('promo');
            stepRef.current = 'promo';
            setRemindError('');
            setRemindLoading(false);
            closedAfterSuccessRef.current = false;
        }
    }, [open]);

    const handleDialogOpenChange = async (next) => {
        // Block any close attempt while the remind-later API is in flight
        if (remindLoading) return;

        if (!next && !closedAfterSuccessRef.current && stepRef.current === 'register') {
            setStep('promo');
            stepRef.current = 'promo';
            setRemindError('');
            return;
        }
        if (!next && !closedAfterSuccessRef.current) {
            setRemindError('');
            setRemindLoading(true);
            try {
                const res = await passkeyRemindLater(userId, PASSKEY_REMIND_AFTER_DAYS);
                if (!isPasskeyRemindLaterSuccess(res)) {
                    setRemindError(res?.message || res?.Message || 'Could not save. Try again.');
                    setRemindLoading(false);
                    return;
                }
            } catch (e) {
                setRemindError(e.message || 'Could not save. Try again.');
                setRemindLoading(false);
                return;
            }
            setRemindLoading(false);
        }
        if (next) setRemindError('');
        onOpenChange(next);
    };

    const handleRegistered = () => {
        closedAfterSuccessRef.current = true;
        clearPasskeyPromptFromStoredUser();
        onRegistered?.();
        setRemindError('');
        // Do NOT call onOpenChange(false) here — onRegistered already triggers
        // gracefulClose → setDialogOpen(false) in the orchestrator, so a second
        // call would queue a second dismissCurrent and skip the next popup.
    };

    const features = [
        { icon: Shield, text: 'Stronger than passwords' },
        { icon: Zap, text: 'Faster, one-tap sign-in' },
        { icon: Smartphone, text: 'Works across your devices' },
    ];

    return (
        <Dialog open={open} onOpenChange={handleDialogOpenChange}>
            <DialogContent
            className={`sm:max-w-md border-0 shadow-xl${remindLoading ? ' [&>button:last-child]:pointer-events-none [&>button:last-child]:opacity-40' : ''}`}
            aria-describedby={undefined}
            onEscapeKeyDown={remindLoading ? (e) => e.preventDefault() : undefined}
        >
                <DialogTitle className="sr-only">Secure your account with a passkey</DialogTitle>
                {!isSupported ? (
                    <>
                        <DialogDescription className="text-center text-sm text-muted-foreground py-6">
                            Your browser does not support passkeys. Please use a modern browser such as Chrome, Edge, or Safari.
                        </DialogDescription>
                        <Button variant="secondary" className="w-full" onClick={() => { void handleDialogOpenChange(false); }}>
                            Close
                        </Button>
                    </>
                ) : step === 'promo' ? (
                    <div className="space-y-5 text-center pt-2">
                        <div className="relative mx-auto flex h-28 w-28 items-center justify-center">
                            <div className="absolute inset-0 rounded-full bg-primary/10 blur-xl" aria-hidden />
                            <Sparkles className="absolute -top-1 -right-1 h-5 w-5 text-primary/40" aria-hidden />
                            <Sparkles className="absolute -bottom-0.5 -left-1 h-4 w-4 text-primary/30" aria-hidden />
                            <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-primary/15 ring-4 ring-primary/10">
                                <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
                                    <UserRound className="h-7 w-7" aria-hidden />
                                    <span className="absolute -right-1 -bottom-1 flex h-8 w-8 items-center justify-center rounded-full bg-background text-primary shadow border border-border">
                                        <KeyRound className="h-4 w-4" aria-hidden />
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <h2 className="text-xl font-semibold text-foreground leading-tight px-1">
                                Secure your account with a passkey
                            </h2>
                            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                                Use a passkey to sign in faster and more securely—no passwords needed.
                            </p>
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-left pt-1">
                            {features.map(({ icon: Icon, text }) => (
                                <div key={text} className="flex flex-col items-center gap-1.5 text-center px-0.5">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                                        <Icon className="h-4 w-4" aria-hidden />
                                    </div>
                                    <p className="text-[11px] sm:text-xs text-muted-foreground leading-snug">{text}</p>
                                </div>
                            ))}
                        </div>

                        {remindError && (
                            <p className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 rounded-md px-3 py-2 text-left">
                                {remindError}
                            </p>
                        )}

                        <div className="space-y-3 pt-1">
                            <Button
                                className="w-full font-semibold"
                                size="lg"
                                disabled={remindLoading}
                                onClick={() => {
                                    stepRef.current = 'register';
                                    setStep('register');
                                }}
                            >
                                Create Passkey
                            </Button>
                            <button
                                type="button"
                                disabled={remindLoading}
                                onClick={() => { void handleDialogOpenChange(false); }}
                                className="inline-flex w-full items-center justify-center gap-2 text-sm font-medium text-primary hover:underline disabled:pointer-events-none disabled:opacity-50"
                            >
                                {remindLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
                                Remind me later
                            </button>
                            <p className="text-xs text-muted-foreground pt-1">
                                You can set this up later in Tools (Manage Passkeys). We'll remind you again in 7 days.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4 pt-2">
                        {remindError && (
                            <p className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 rounded-md px-3 py-2">
                                {remindError}
                            </p>
                        )}
                        <div>
                            <h2 className="text-lg font-semibold text-foreground">Create your passkey</h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                Give this device a name, then confirm when your browser asks.
                            </p>
                        </div>
                        <PasskeyRegisterPanel
                            userId={userId}
                            username={username}
                            showExistingDeviceHint={false}
                            submitLabel="Create Passkey"
                            onCancel={() => {
                                stepRef.current = 'promo';
                                setStep('promo');
                            }}
                            onSuccess={handleRegistered}
                        />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
